/**
 * Canonical Axiom event shape shared by every ingest path.
 *
 * Axiom datasets have a hard cap on distinct field names (256 on the free
 * plan), and every top-level key — including each key of a nested object,
 * which Axiom flattens to `parent.child` — permanently occupies one slot.
 * All event-specific keys therefore live under `data`, which a project
 * configures as a **map field** on its dataset so the nested keys never
 * count toward the limit. See logging-strategy POLICY.md §12.
 *
 * The name is Axiom's, but the shape is not: a non-Axiom sink is just
 * another `LogBackend` consuming this same canonical event, and `_time` is
 * the only field Axiom itself contributes.
 *
 * Rules:
 * - Never spread an entry (or any arbitrary object) into the root of an
 *   event — every new key becomes a dataset field forever.
 * - Adding a top-level field is a deliberate schema decision: add it here
 *   and to `BaseLogEntry`, nowhere else.
 * - Every ingest path MUST emit this exact shape so queries
 *   (`userId == …`, `['data']['duration_ms']`) behave identically
 *   regardless of where the event originated.
 */

import type { DeployTarget } from "@bindery/topology";
import type { BaseLogEntry, LogError, LogLevel } from "./types";

/**
 * The event object ingested into the sink. Field-for-field this is
 * `BaseLogEntry` plus Axiom's `_time`; it exists as its own type so the
 * top-level field budget is explicit and closed.
 */
export interface AxiomEvent {
  /** Axiom's canonical timestamp field (ISO 8601). */
  _time: string;
  level: LogLevel;
  eventName: string;
  eventId: string;
  timestamp: number;
  environment: string;
  service: string;
  version?: string;
  sessionId?: string;
  userId?: string;
  readerId?: string;
  leadId?: string;
  visitId?: string;
  /**
   * Which deployment emitted this event (kanban #27) — NOT a `BaseLogEntry`
   * field, and `toAxiomEvent` below never sets it. Every other field here
   * comes from the client, which cannot answer this one truthfully: a built
   * bundle is the same bundle for dev, preview and prod (LOG_SERVICE_NAME is
   * pinned per app, not per target, and import.meta.env.MODE is
   * "production" for all three). Only the ingest boundary that receives the
   * request knows which Worker it is — see the `deployTarget` parameter on
   * `@bindery/logging/ingest`'s `partitionLogEntries`/`clampEventTime`,
   * which stamp it in after schema validation, the same way `_time` is
   * clamped there. Present only on events that passed through that
   * boundary; a console-only build never sets it.
   */
  deployTarget?: DeployTarget;
  /** Event-specific payload — stored as an Axiom map field. */
  data?: Record<string, unknown>;
  /**
   * Fixed three-key error object; safe as regular (non-map) fields.
   * Enumerated key-by-key in `toAxiomEvent` — extra keys on a caller's
   * `LogError` must not leak into the dataset schema.
   */
  error?: LogError;
}

/**
 * Convert a log entry to the canonical event.
 *
 * Fields are enumerated explicitly (no spread) so that a future addition
 * to `BaseLogEntry` cannot silently create a new dataset field.
 */
export function toAxiomEvent(entry: BaseLogEntry): AxiomEvent {
  return {
    _time: new Date(entry.timestamp).toISOString(),
    level: entry.level,
    eventName: entry.eventName,
    eventId: entry.eventId,
    timestamp: entry.timestamp,
    environment: entry.environment,
    service: entry.service,
    version: entry.version,
    sessionId: entry.sessionId,
    userId: entry.userId,
    readerId: entry.readerId,
    leadId: entry.leadId,
    visitId: entry.visitId,
    data: entry.data,
    error: entry.error
      ? {
          type: entry.error.type,
          message: entry.error.message,
          code: entry.error.code,
        }
      : undefined,
  };
}

/** BigInt has no JSON form; its decimal string keeps the value. */
function jsonSafe(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? value.toString() : value;
}

/**
 * `JSON.stringify(events)`, and never a throw.
 *
 * `data` is the caller's, and a circular reference in it (or a BigInt, which
 * JSON has no form for) made JSON.stringify throw inside the backend. The
 * backends caught that and returned, so the event was neither sent nor
 * handed to Logger's console fallback — it vanished. A BigInt is now written
 * as its decimal string. Anything else that will not serialize costs only the
 * `data` bag: the event goes out with `data` replaced by
 * `{ data_unserializable: <the error's name> }`, so its name, level, error
 * and correlation fields still arrive.
 *
 * Generic over the event type so the Worker logger's narrower line
 * (./worker-logger.ts) gets the same never-throw guarantee.
 */
export function serializeEvents<E extends { data?: unknown } = AxiomEvent>(
  events: readonly E[],
): string {
  try {
    return JSON.stringify(events, jsonSafe);
  } catch (error) {
    const reason = error instanceof Error ? error.name : "Error";
    return JSON.stringify(
      events.map((event) => {
        try {
          JSON.stringify(event, jsonSafe);
          return event;
        } catch {
          return { ...event, data: { data_unserializable: reason } };
        }
      }),
      jsonSafe,
    );
  }
}
